import React from 'react';
import { Box, Typography, CardActionArea } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const isVideoSource = (value = '') => {
    const v = String(value || '').toLowerCase();
    return /\.(mp4|webm|ogg)(\?.*)?$/i.test(v) || v.includes('youtube') || v.includes('vimeo');
};

const resolveGalleryUrl = (entry) => {
    if (!entry) return '';
    if (typeof entry === 'string') return entry;
    return entry.original || entry.url || entry.src || '';
};

/**
 * NEWS JOURNAL CARD (V9.36)
 * Aesthetic: Cinematic Editorial
 * Features: 
 * - Magnetic Lift (Scale + Translate)
 * - Shadow Bloom
 * - Strict 16:9 Aspect Ratio
 * - Clamped Typography (Inter)
 */
const NewsJournalCard = ({ item }) => {
    const stripToExcerpt = (value, maxLen = 180) => {
        const raw = (value || '').toString();
        const text = raw
            .replace(/<[^>]*>/g, ' ') // naive HTML strip
            .replace(/\s+/g, ' ')
            .trim();
        if (!text) return '';
        if (text.length <= maxLen) return text;
        return `${text.slice(0, maxLen - 1)}…`;
    };

    const dateValue = item?.publishedAt || item?.updatedAt || null;
    const dateLabel = dateValue
        ? new Date(dateValue).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase()
        : '';

    const excerpt = stripToExcerpt(item?.subtitle || item?.content, 210);
    const primaryMediaUrl =
        item?.image ||
        (Array.isArray(item?.gallery) && item.gallery.length ? resolveGalleryUrl(item.gallery[0]) : '') ||
        '/images/hero.png';
    const isPrimaryVideo = isVideoSource(primaryMediaUrl);

    return (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '24px', // Architectural Radius
                overflow: 'hidden',
                bgcolor: 'common.white',
                border: '1px solid',
                borderColor: 'grey.200',
                transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                position: 'relative',
                    '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
                        borderColor: 'grey.300',
                    '& .card-media': {
                        transform: 'scale(1.05)',
                    },
                    '& .read-more-icon': {
                        transform: 'translateX(4px)',
                        opacity: 1
                    }
                }
            }}
        >
            <CardActionArea 
                component={RouterLink} 
                to={`/news/${item.id}`} 
                sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start'
                }}
            >
                {/* Image Container */}
                <Box sx={{ 
                    width: '100%', 
                    aspectRatio: '16/9', 
                    position: 'relative', 
                    overflow: 'hidden',
                    flexShrink: 0,
                    borderBottom: '1px solid',
                    borderColor: 'grey.100'
                    }}>
                        {isPrimaryVideo ? (
                            <Box
                                component="video"
                                className="card-media"
                                src={primaryMediaUrl}
                                autoPlay
                                loop
                                muted
                                playsInline
                                preload="metadata"
                                aria-label={item.title || 'Vídeo da notícia'}
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    transition: 'transform 0.6s cubic-bezier(0.33, 1, 0.68, 1)',
                                    pointerEvents: 'none',
                                }}
                            />
                        ) : (
                            <Box
                                component="img"
                                className="card-media"
                                src={primaryMediaUrl}
                                alt={item.title}
                                onError={(e) => {
                                    if (e.currentTarget && e.currentTarget.src && e.currentTarget.src.includes('/images/hero.png')) return;
                                    e.currentTarget.src = '/images/hero.png';
                                }}
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    transition: 'transform 0.6s cubic-bezier(0.33, 1, 0.68, 1)',
                                }}
                            />
                        )}
                    
                    {/* Date Badge */}
                    {dateLabel && (
                        <Box sx={{
                            position: 'absolute',
                            top: 16,
                            left: 16,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            backdropFilter: 'blur(8px)',
                            color: 'white',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '14px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            letterSpacing: '0.05em'
                        }}>
                            {dateLabel}
                        </Box>
                    )}
                </Box>

                {/* Content Container */}
                <Box sx={{ 
                    p: 4, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    flexGrow: 1, 
                    width: '100%',
                    position: 'relative'
                }}>
                    {/* Category/Eyebrow */}
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            color: 'primary.main', 
                            fontWeight: 700, 
                            letterSpacing: '0.1em', 
                            mb: 1.5,
                            display: 'block',
                            fontSize: '0.7rem'
                        }}
                    >
                        R5 NEWS
                    </Typography>

                    {/* Title */}
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            fontWeight: 700, 
                            mb: 2, 
                            lineHeight: 1.3, 
                            fontSize: '1.25rem',
                            fontFamily: 'Inter, sans-serif',
                            color: 'grey.900',
                            // Clamp 2 lines
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            minHeight: 'calc(1.3 * 1.25rem * 2)', // Minimum height for alignment
                        }}
                    >
                        {item.title}
                    </Typography>

                    {/* Excerpt */}
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            color: 'text.secondary', 
                            lineHeight: 1.6,
                            fontSize: '0.95rem',
                            mb: 3,
                            // Clamp 3 lines
                            display: '-webkit-box', 
                            WebkitLineClamp: 3, 
                            WebkitBoxOrient: 'vertical', 
                            overflow: 'hidden',
                            flexGrow: 1 
                        }}
                    >
                        {excerpt}
                    </Typography>

                    {/* Footer / Read More */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                        <Typography 
                            variant="button" 
                            sx={{ 
                                color: 'common.black', 
                                fontWeight: 700, 
                                fontSize: '0.85rem',
                                letterSpacing: '0.05em'
                            }}
                        >
                            LER ARTIGO
                        </Typography>
                        <ArrowForwardIcon 
                            className="read-more-icon"
                            sx={{ 
                                ml: 1, 
                                fontSize: '1rem', 
                                color: 'primary.main',
                                opacity: 0.5,
                                transition: 'all 0.3s ease'
                            }} 
                        />
                    </Box>
                </Box>
            </CardActionArea>
        </Box>
    );
};

export default NewsJournalCard;
